import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import type { RoutePlan, RouteAssignment } from '@/api/route-plans';

export interface CaregiverAssignments {
  name: string;
  assignments: RouteAssignment[];
}

export type AssignmentsByCaregiver = Record<number, CaregiverAssignments>;

function groupAssignments(assignments: RouteAssignment[]): AssignmentsByCaregiver {
  const groups: AssignmentsByCaregiver = {};
  for (const a of assignments) {
    groups[a.caregiverId] ||= { name: a.caregiverFullName, assignments: [] };
    groups[a.caregiverId]!.assignments.push(a);
  }

  return groups;
}

// Include caregivers who only have existing shifts (no new visits) so their shifts —
// especially ones the optimizer couldn't place — are never hidden from the route views.
function includeExistingShiftCaregivers(groups: AssignmentsByCaregiver, plan: RoutePlan): void {
  const nameById = new Map(
    (plan.routePlanCaregivers || []).map(c => [c.caregiverId, c.caregiverFullName]),
  );
  for (const cgId of Object.keys(plan.caregiverExistingShifts || {}).map(Number)) {
    groups[cgId] ||= { name: nameById.get(cgId) || '', assignments: [] };
  }
}

// Groups a plan's assignments per caregiver, sorted by route sequence. Shared by
// the map legend, the timeline and the per-caregiver route list.
export function useAssignmentsByCaregiver(
  routePlan: MaybeRefOrGetter<RoutePlan>,
): ComputedRef<AssignmentsByCaregiver> {
  return computed(() => {
    const plan = toValue(routePlan);
    const groups = groupAssignments(plan.routeAssignments || []);
    includeExistingShiftCaregivers(groups, plan);

    for (const group of Object.values(groups)) {
      group.assignments.sort((x, y) => x.sequence - y.sequence);
    }

    return groups;
  });
}
